import {DataFetcher} from '@aboutyou/data-fetcher';
import {AddToBasketTrackingData} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {memo} from 'react';
import {useStaleLastSeenProductIds} from '../../../features/LastSeenProducts/useLastSeenProductIds';
import {productInfoType} from '../../../features/OutfitSlider/OutfitSlider';
import {ProductTileProps} from '../../../features/ProductTile/ProductTile';
import {useProductIdFromSlug} from '../../../features/ProductUrl';
import {useShopTheLookScrollManager} from '../ShopTheLookScrollIntoView/useShopTheLookScrollManager';
import {RecommendationsView} from './RecommendationsView';
import {Skeleton} from './Skeleton';
import {useIsRecoRequestEnabled} from './utils/useIsRecoRequestEnabled';
import {useRecommendationsData} from './utils/useRecommendationsData';

const UNIQUE_RECOMMENDATION_LAST_SEEN_PRODUCTS_KEY =
  'UNIQUE_RECOMMENDATION_LAST_SEEN_PRODUCTS_KEY';

export interface RecommendationsConfiguration {
  gridColumns?: number;
  uniqueKey?: string;
  compactMode?: boolean;
  ProductTileComponent?: React.FC<ProductTileProps>;
  showInlineOutfitSlider?: boolean;
  setRecoType?: (recoType: string) => void;
  preloadOutfitDetailPage?: VoidFunction;
  setAddToBasketTrackingData?: (params: AddToBasketTrackingData) => void;
}

export const Recommendations: React.FC<
  {
    onAddToBasketCtaClick?: ({
      productId,
      locationId,
      couponPromotionId,
    }: {
      productId: number;
      locationId?: string;
      couponPromotionId?: string;
    }) => void;
    className?: string;
    productInfo?: productInfoType;
  } & RecommendationsConfiguration
> = ({className, ...props}) => {
  const {shouldScrollShopTheLookIntoView} = useShopTheLookScrollManager();

  const {ref, requestEnabled} = useIsRecoRequestEnabled<HTMLDivElement>(
    shouldScrollShopTheLookIntoView,
  );
  const lastSeenProductIds = useStaleLastSeenProductIds({
    uniqueKey: UNIQUE_RECOMMENDATION_LAST_SEEN_PRODUCTS_KEY,
  });

  // NaN is a fallback to make the type check work, it should technically never happen if this is only used on ADP as expected
  const productId = useProductIdFromSlug() || NaN;

  return (
    <RecommendationsWrapper
      ref={ref}
      data-testid="recosFetchTrigger"
      className={className}
    >
      <DataFetcher
        request={useRecommendationsData({
          requestEnabled,
          lastSeenProductIds,
        })}
        Error={null}
        Loading={MemoSkeleton}
        viewProps={{...props, productId}}
        View={RecommendationsView}
      />
    </RecommendationsWrapper>
  );
};

const MemoSkeleton = memo(Skeleton);

const RecommendationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
